import { RefreshOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InventoryIcon from "@mui/icons-material/Inventory";
import TokenIcon from "@mui/icons-material/Token";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Stack,
  StackProps,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { assetService, basketService, cacheService, setupService } from "../../../../Core/Api/services";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { AssetSubType } from "../../../../Core/Api/Types/asset-sub-type";
import { BasketItemStatus } from "../../../../Core/Api/Types/basket-item-status";
import { DbAssetObjectInfos } from "../../../../Core/Api/Types/db-asset-object-infos";
import { DbCultureInfos } from "../../../../Core/Api/Types/db-culture-infos";
import {
  DbOrderBasketItemInfos,
  TranskodeBundleByIdentityProfile,
} from "../../../../Core/Api/Types/db-order-basket-item-infos";
import { EnrichedMetadata } from "../../../../Core/Api/Types/enriched-metadata";
import { Licences } from "../../../../Core/Api/Types/licences";
import { ObjectStatus } from "../../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { PlatformLanguageCode } from "../../../../Core/Api/Types/platform-language-code";
import { TranskodeBundleAsset } from "../../../../Core/Api/Types/transkode-bundle-asset";
import { TranskodeBundleComponent } from "../../../../Core/Api/Types/transkode-bundle-component";
import { TranskodeBundleComponentType } from "../../../../Core/Api/Types/transkode-bundle-component-type";
import { TranskodeVideoProfile } from "../../../../Core/Api/Types/transkode-video-profile";
import { XmlBundleDeliveryIdentityProfile } from "../../../../Core/Api/Types/xml-bundle-delivery-identity-profile";
import { GetMetadataByTagName } from "../../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import {
  BundleMakerAddComponent,
  BundleMakerAddComponentAsset,
  BundleMakerRemoveComponent,
  BundleMakerRemoveComponentAssetAll,
  GetBasketBundleProfiles,
  ProductField,
  SettingRootField,
  UpdateItemProductRootOnBasket,
  UpdateItemSettingsRootOnBasket,
} from "../../../../Redux/Reducers/redux-slice-basket";
import { GetSectionIsValid, SetFieldIsValid } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../../Redux/redux-store";
import { getAssetCultures, getBundlesContent } from "../../../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import { GetMainLanguageOfCountry, OpenLinkInNewTab, UserHasAcl, UserHasLicence } from "../../../../Utils/helpers";
import {
  IsAudioMappingIV,
  SelectBundleProfile,
} from "../../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import {
  BundleMetadataValidationStatus,
  checkValidationByCountry,
} from "../../../../Utils/Metadata/utils-metadata-validation";
import FormAutocomplete from "../../../Forms/FormAutocomplete/form-autocomplete";
import FormInputText from "../../../Forms/FormInputText/form-input-text";
import FormSection from "../../../Forms/FormSection/form-section";
import AssetStatus from "../../../Misc/Status/AssetStatus/asset-status";
import { ModalContext } from "../../../Modals";
import ModalComposePackageBundle, {
  ModalComposePackageBundleElementProps,
} from "../../../Modals/ModalComposePackageBundle/modal-compose-package-bundle";
import ConfigCommonSectionFormAttachments from "../../ConfigCommon/config-common-section-form-attachments";
import ConfigFormIcon from "../../ConfigFormIcon";
import ConfigPackageBundleComponent from "../ConfigPackageBundleComponent";
import ConfigPackageBundleMetadata from "../ConfigPackageBundleMetadata";
import ConfigCommonSectionFormNexguardWatermark from "../../ConfigCommon/config-common-section-form-nexguard-watermark";
import ConfigCommonSectionFormAssetsVisible from "../../ConfigCommon/config-common-section-form-assets-visible";
import { useTranslation } from "react-i18next";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";

interface ConfigPackageBundleFormProps extends StackProps {
  formid: string;
  item: DbOrderBasketItemInfos;
}

function usePrevious(value: number) {
  const ref = useRef<number>(value);
  useEffect(() => {
    ref.current = value; //assign the value of ref to the argument
  }, [value]); //this code will run when the value of 'value' changes
  return ref.current; //in the end, return the current ref value.
}

const ConfigPackageBundleForm: React.FC<ConfigPackageBundleFormProps> = (props) => {
  const { t } = useTranslation();
  const { formid, item, ...otherProps } = props;
  const modalContext = React.useContext(ModalContext);
  const profiles = useAppSelector(GetBasketBundleProfiles);
  const { openModal } = { ...modalContext };
  const [xmlProfile, setXmlProfile] = useState<XmlBundleDeliveryIdentityProfile | undefined>(undefined);
  const [cultures, setCultures] = useState<DbCultureInfos[]>([]);
  const [metadata, setMetadata] = useState<EnrichedMetadata[]>([]);
  const [availablePackages, setAvailablePackages] = useState<DbAssetObjectInfos[]>([]);
  const [loadPackageTrigger, setLoadPackageTrigger] = useState<number>(1);
  const [selectedTitleOriginalLang, setSelectedTitleOriginalLang] = useState<PlatformLanguageCode | null>(null);

  const [addComponentValue, setAddComponent] = useState<TranskodeBundleComponentType | undefined>(undefined);
  const dispatch = useDispatch();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const epNbMetadata = cacheService.GetMetadata().find((x) => x.tagName === "serieEpisodeNumberProduction");
  const broadcasterIdMeta = cacheService.GetMetadata().find((x) => x.tagName === "broadcasterId");

  const settings: TranskodeBundleByIdentityProfile = item.selectedProduct?.settings;

  const prevComponentsCount: number = usePrevious(settings.components!.length);

  const canUseBundlePackages = useMemo(
    () =>
      (item.titleId ?? item.titleInfos?.id) &&
      ((xmlProfile?.providerInfos?.manifestExportSessionNames &&
        xmlProfile.providerInfos.manifestExportSessionNames.length > 0) as boolean) &&
      (UserHasAcl(AclTypes.aclSuperUser) || UserHasLicence(Licences.BundleMakerMetadata)),
    [item.titleId ?? item.titleInfos?.id, xmlProfile?.providerInfos?.manifestExportSessionNames]
  );

  // Update setting root base object into redux
  const handleUpdateSettingsRoot = (field: SettingRootField, value: any) => {
    dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Update product root base object into redux
  const handleUpdateProductRoot = (field: ProductField, value: any) => {
    dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  // Memo
  const _titleOriginalLang: PlatformLanguageCode = useMemo(() => {
    return (
      GetMetadataByTagName(item?.titleInfos, "titleOriginalLanguage")?.value || {
        label: "",
        value: "",
        rfc5646: "",
        id: -1,
        externalId: -1,
      }
    );
  }, [item.titleInfos]);

  const allTitleOriginalLanguages: PlatformLanguageCode[] = useMemo(() => {
    const output: PlatformLanguageCode[] = [];

    if (_titleOriginalLang) output.push(_titleOriginalLang);

    const secondaryLangs = GetMetadataByTagName(item?.titleInfos, "titleSecondaryOriginalLanguages")?.value;
    if (secondaryLangs) output.push(...secondaryLangs);

    if (output.length === 0) return [{ label: "", value: "", rfc5646: "", id: -1, externalId: -1 }];

    return output;
  }, [_titleOriginalLang, item?.titleInfos]);

  const titleOriginalLang: PlatformLanguageCode = useMemo(() => {
    if (selectedTitleOriginalLang) return selectedTitleOriginalLang;

    return _titleOriginalLang;
  }, [selectedTitleOriginalLang, _titleOriginalLang]);

  const isOriginalLanguageInCountries = useMemo((): boolean => {
    if (!settings || !titleOriginalLang || !settings.countries || settings.countries.length === 0) return false;

    for (let i = 0; i < settings.countries.length; ++i) {
      for (let l = 0; l < settings.countries[i].languages!.length; ++l) {
        let lang = settings!.countries[i]!.languages![l];

        if (!lang || !lang.isoCodeLocalized || !titleOriginalLang.rfc5646) continue;

        if (lang.isoCodeLocalized.toLowerCase().indexOf("-" + titleOriginalLang.rfc5646?.toLowerCase()) >= 0)
          return true;
        if (lang.isoCodeLocalized.toLowerCase().startsWith(titleOriginalLang!.rfc5646!.toLowerCase())) return true;
      }
    }

    return false;
  }, [titleOriginalLang, settings.countries]);

  const lockComposer = useMemo((): boolean => {
    return !settings.countries || settings.countries.length === 0;
  }, [settings.countries]);

  const metadataSource = useMemo((): EnrichedMetadata[] => {
    /* 2 sources for metadata:
        -> first is default from backend
        -> second is from product if already set
        */

    let list: EnrichedMetadata[] = [];

    if (settings.metadata && settings.metadata.length > 0 && metadata && metadata.length > 0) {
      settings.metadata.forEach((meta) => {
        let matchedDefault = metadata.find((x) => x.field?.id === meta.id)!;
        if (!matchedDefault) return;
        list.push({
          usage: matchedDefault.usage,
          field: matchedDefault.field,
          value: meta,
        });
      });
      console.debug("bundlemaker:: metadata editor src from product");
    } else {
      list = metadata;
      console.debug("bundlemaker:: metadata editor src from default");
    }

    return list;
  }, [settings.metadata, metadata]);

  const deliveryAttachments = useMemo(() => {
    return xmlProfile?.deliveryInfos?.find((x) => x.assetType === ObjectType.Document);
  }, [xmlProfile]);

  // Funcs
  const makeDefaultBundle = () => {
    if (!xmlProfile) return;

    for (let i = 0; i < settings.components.length; i++) {
      console.debug("Making component " + settings.components[i].type);

      const indexOfComponent = xmlProfile.components?.findIndex((x) => x.type === settings.components[i].type);

      if (indexOfComponent === -1) return;

      const assets: TranskodeBundleAsset[] = [];

      dispatch(BundleMakerRemoveComponentAssetAll({ itemId: item.id, componentIndex: i, assetIndex: -1 }));

      const processedAssetsVideoIndexes: number[] = [];

      for (let a = 0; a < xmlProfile.components[indexOfComponent].assets.length; a++) {
        if (xmlProfile.components[indexOfComponent].assets[a].maxItems === 0) continue;

        switch (xmlProfile.components[indexOfComponent].assets[a].type) {
          case ObjectType.Video:
          case ObjectType.Audio:
          case ObjectType.Subtitle:
            if (settings.components[i].type === TranskodeBundleComponentType.Artwork) break;

            // Add video assets for Original Language (as original languages assets will be bypassed later)
            if (
              xmlProfile.components[indexOfComponent].assets[a].type === ObjectType.Video &&
              isOriginalLanguageInCountries
            ) {
              assets.push(Object.assign({}, xmlProfile.components[indexOfComponent].assets[a]));
              processedAssetsVideoIndexes.push(a); // Used to avoid adding multiple assets for same video when adding countries
              continue;
            }

            // Add subtitle assets for Original Language
            if (
              settings.components[i].type === TranskodeBundleComponentType.Feature &&
              isOriginalLanguageInCountries &&
              xmlProfile.components[indexOfComponent].assets[a].type === ObjectType.Subtitle &&
              assets.filter((x) => x.type === ObjectType.Subtitle && x.country === null).length === 0
            ) {
              assets.push(Object.assign({}, xmlProfile.components[indexOfComponent].assets[a]));
            }

            // Add assets for each country
            for (let c = 0; c < settings.countries?.length; c++) {
              // Subtitles will be added later in modal composer depending of video selection
              if (xmlProfile.components[indexOfComponent].assets[a].type === ObjectType.Subtitle) continue;

              // Bypass Worldwide countries for Features
              if (
                settings.components[i].type === TranskodeBundleComponentType.Feature &&
                settings.countries[c].languages!.filter(
                  (x) =>
                    x.isoCodeLocalized &&
                    (x.isoCodeLocalized?.toLowerCase().indexOf("-" + titleOriginalLang.rfc5646?.toLowerCase()) >= 0 ||
                      (titleOriginalLang.rfc5646 &&
                        x.isoCodeLocalized.toLowerCase().startsWith(titleOriginalLang.rfc5646.toLowerCase())))
                ).length > 0
              )
                continue;

              const asset = Object.assign({}, xmlProfile.components[indexOfComponent].assets[a]);

              // Bypass already added video assets for each country (in case they were added for Original Language)
              if (asset.type === ObjectType.Video) {
                if (processedAssetsVideoIndexes.includes(a)) continue;
                else processedAssetsVideoIndexes.push(a);
              }

              // If asset is ONLY Worldwide, add only one asset (no need to create multiple assets as we use title Original Lang only)
              if (asset.useWorldWide) {
                assets.push(asset);
                break;
              }

              let isAudioIV = false;

              if (
                xmlProfile.components[indexOfComponent].assets[a].type === ObjectType.Audio &&
                xmlProfile.components[indexOfComponent].assets[a].audioInfos !== null
              )
                isAudioIV = IsAudioMappingIV(xmlProfile.components[indexOfComponent].assets[a].audioInfos!.mappings!);

              if (!isAudioIV) asset.country = Object.assign({}, settings.countries[c]);
              else {
                if (c > 0) continue; // no need VI or M&E for each country.
                asset.subType = AssetSubType.AudioMusicAndEffects;
              }

              assets.push(asset);
            }
            break;
          case ObjectType.Image:
            if (settings.components[i].type === TranskodeBundleComponentType.DubCard) {
              for (let c = 0; c < settings.countries?.length; c++) {
                let asset = Object.assign({}, xmlProfile.components[indexOfComponent].assets[a]);
                asset.country = Object.assign({}, settings.countries[c]);
                assets.push(asset);
              }
            }
            if (settings.components[i].type === TranskodeBundleComponentType.Artwork) {
              for (let c = 0; c < settings.countries?.length; c++) {
                let asset = Object.assign({}, xmlProfile.components[indexOfComponent].assets[a]);
                asset.country = Object.assign({}, settings.countries[c]);

                let isEpisode: boolean = GetMetadataByTagName(item.titleInfos, "titleType")?.value === "Season";
                if (isEpisode) {
                  switch (asset.subType) {
                    case AssetSubType.ArtworkPosterVertical:
                    case AssetSubType.ArtworkPosterHorizontal:
                    case AssetSubType.ArtworkSquare:
                    case AssetSubType.ArtworkPoster169:
                    case AssetSubType.ArtworkPosterHero:
                    case AssetSubType.ArtworkPoster23:
                      asset.context = "Episode";
                      break;
                    case AssetSubType.ArtworkSeriePosterVertical:
                    case AssetSubType.ArtworkSeriePosterHorizontal:
                    case AssetSubType.ArtworkSeriePosterSquare:
                    case AssetSubType.ArtworkSeriePoster169:
                      asset.context = "Serie";
                      break;
                    case AssetSubType.ArtworkSeasonPosterVertical:
                    case AssetSubType.ArtworkSeasonPosterHorizontal:
                    case AssetSubType.ArtworkSeasonPosterSquare:
                    case AssetSubType.ArtworkSeasonPoster169:
                      asset.context = "Season";
                      break;
                    default:
                      // Handle other cases if needed
                      break;
                  }
                }
                assets.push(asset);
              }
            }
            break;
        }
      }

      assets.forEach((asset) => {
        asset.language = GetMainLanguageOfCountry(asset.country!)!;
        dispatch(BundleMakerAddComponentAsset({ itemId: item.id, componentIndex: i, asset: asset }));
      });
    }

    validateComposer(false);
    console.info("::bundlemaker: makeDefault() done");
  };
  // Bundle Composer
  const showModalComposePackageComponent = React.useCallback(
    (component: TranskodeBundleComponent) => {
      if (!openModal) return;

      openModal<ModalComposePackageBundleElementProps>(ModalComposePackageBundle, {
        needComputeDefault: item.status !== BasketItemStatus.OptionsDone,
        components: settings.components,
        preselectedComponent: component,
        targetFeatureVideoProfile: settings.targetFeatureVideoProfile ?? TranskodeVideoProfile.HD,
        titleOriginalLang: titleOriginalLang,
        titleInfos: item.assetInfos!,
        xmlProfile: xmlProfile!,
        countries: settings.countries,
        item: item,
        onClose(props) {
          if (props.components) {
            handleUpdateSettingsRoot(SettingRootField.Components, props.components);

            console.info("bundlemaker::config done");
            validateComposer(true);
          }
        },
      });
    },
    [
      openModal,
      settings.components,
      settings.countries,
      settings.targetFeatureVideoProfile,
      xmlProfile,
      titleOriginalLang,
      item,
      item.status,
      item.assetInfos,
    ]
  );

  // Form validator
  const formSectionComposer = useAppSelector(GetSectionIsValid(formid, "sectionComposer"));
  const formSectionMetadata = useAppSelector(GetSectionIsValid(formid, "sectionMetadata"));

  const validateComposer = (valid: boolean) => {
    dispatch(
      SetFieldIsValid({
        formId: formid,
        formSectionId: "sectionComposer",
        fieldId: "composer",
        fieldIsValid: valid,
        fieldErrorMessage: !valid
          ? t("field:errorMessage.composeYourPackage")
          : t("field:validMessage.packageComposerIsReady"),
      })
    );
  };

  const validateMetadata = (valid: boolean) => {
    dispatch(
      SetFieldIsValid({
        formId: formid,
        formSectionId: "sectionMetadata",
        fieldId: "meta",
        fieldIsValid: valid,
        fieldErrorMessage: !valid
          ? t("field:errorMessage.checkAndFillMetadata")
          : t("field:validMessage.packageMetadataAreReady"),
      })
    );
  };

  // Effects
  // First load
  useEffect(() => {
    console.log("product::bundlebyprofile:: broadcasterId = " + item.selectedBroadcaster?.id);
    if (profiles && item.selectedBroadcaster?.id) {
      if (!titleOriginalLang || !titleOriginalLang.value) {
        console.log("product::bundlebyprofile:: titleOriginalLang is undefined");
        enqueueCustomSnackbar(t("alert:error.missingOriginalTitleLanguageMetadata"), "error");
      }

      let profile = SelectBundleProfile(profiles, item.selectedBroadcaster.id, titleOriginalLang.value ?? "")?.profile;
      if (profile) setXmlProfile(profile);
    }

    setupService.GetAllCultures().then((response) => {
      if (response.cultures)
        setCultures(response.cultures.sort((a, b) => (a.englishName || "").localeCompare(b.englishName || "")));
    });

    // Validate or Invalidate bundle composer based on last save (so item status)
    validateComposer(item.status === BasketItemStatus.OptionsDone);
  }, [item.selectedBroadcaster?.id, profiles, titleOriginalLang]);

  useEffect(() => {
    if (settings.countries && settings.targetFeatureVideoProfile) makeDefaultBundle();
  }, [settings.countries, settings.targetFeatureVideoProfile, titleOriginalLang]);

  useEffect(() => {
    // Get prepared metadata from backend
    if (!settings.countries || settings.countries.length === 0) return;

    if (settings.objectId) {
      const selectedPackage = availablePackages.find((x) => x.id === settings.objectId);

      if (!selectedPackage) return;

      const packageBroadcasterId = selectedPackage.metadata?.find((x) => x.id === broadcasterIdMeta?.id)?.value;

      if (!packageBroadcasterId) return;

      assetService
        .GetBundleMakerComputeCulturedMetadata({
          objectId: selectedPackage.id,
          broadcasterId: packageBroadcasterId,
          cultureIds: settings.countries.map((c) => c.id),
          cultureLanguageOverrides: null,
        })
        .then((res) => {
          if (res.success && res.metadata) {
            handleUpdateSettingsRoot(
              SettingRootField.Metadata,
              res.metadata.map((x) => x.value)
            );

            console.debug("bundlemaker:: metadata fetched ok");
            // Potentially patch new initialized fields
            res.metadata.forEach((m) => {
              if (!m.value!.fieldName) {
                m.value!.id = m.field!.id;
                m.value!.fieldLabel = m.field!.label;
                m.value!.fieldName = m.field!.tagName;
                m.value!.targetLayer = m.field!.layer;
              }
            });
            setMetadata(res.metadata);
          }
        });
    } else {
      basketService
        .GetBasketItemComputeBundleMakerMetadata({
          itemId: item.id,
          cultureIds: settings.countries.map((c) => c.id),
        })
        .then((response) => {
          if (response.metadata) {
            handleUpdateSettingsRoot(
              SettingRootField.Metadata,
              response.metadata.map((x) => x.value)
            );

            console.debug("bundlemaker:: metadata fetched ok");

            // Shallow copy metadata : can't set properties as it is readonly
            const responseMetadata = JSON.parse(JSON.stringify(response.metadata));

            // Potentially patch new initialized fields
            responseMetadata.forEach((m) => {
              if (!m.value!.fieldName) {
                m.value!.id = m.field!.id;
                m.value!.fieldLabel = m.field!.label;
                m.value!.fieldName = m.field!.tagName;
                m.value!.targetLayer = m.field!.layer;
              }
            });
            setMetadata(responseMetadata);
          }
        });
    }
  }, [settings.countries, settings.objectId]);

  useEffect(() => {
    if (metadataSource.length === 0) {
      validateMetadata(true);
      return;
    }

    if (!checkValidationByCountry(null, metadataSource)) {
      validateMetadata(false);
      return;
    }

    if (settings.countries) {
      for (var i = 0; i < settings.countries.length; ++i) {
        if (!checkValidationByCountry(settings.countries[i], metadataSource)) {
          validateMetadata(false);
          return;
        }
      }
    }

    validateMetadata(true);
  }, [metadataSource, settings.countries]);

  const getAvailablePackages = (id: number) => {
    assetService.GetObjectLightById(id, 2).then((res) => {
      if (res.success && res.graph) {
        const bundleContent = getBundlesContent(res.graph);
        setAvailablePackages(
          bundleContent?.childObjects?.filter((x) => ![ObjectStatus.Deleted].includes(x.status)) ?? []
        );
      }
    });
  };

  useEffect(() => {
    canUseBundlePackages && getAvailablePackages(item.titleId ?? item.titleInfos!.id);
  }, [canUseBundlePackages, item.selectedBroadcaster?.id, loadPackageTrigger]);

  useEffect(() => {
    if (xmlProfile && !settings.components.some((x) => x.assets !== null)) {
      makeDefaultBundle();
    }
  }, [xmlProfile]);

  useEffect(() => {
    if (allTitleOriginalLanguages.length > 0) setSelectedTitleOriginalLang(allTitleOriginalLanguages[0]);
  }, [allTitleOriginalLanguages]);

  return (
    <StyledForm>
      <Stack spacing={1.5} width={"100%"} {...otherProps}>
        <FormSection
          icon={<ConfigFormIcon icon="version" />}
          title={t("form:section.title.originalVersionDetail")}
          status={titleOriginalLang && titleOriginalLang.value ? "details" : "warning"}
          statusText={
            titleOriginalLang && titleOriginalLang.value
              ? `${titleOriginalLang.label}`
              : t("form:statusText.missingOriginalTitleLanguageMetadata")
          }
          defaultExpanded={false}
        >
          <Stack direction="row" alignItems="center">
            {t("common:language")}&nbsp;
            <Chip
              label={`${titleOriginalLang.label} - ${titleOriginalLang.value} (RFC ${titleOriginalLang.rfc5646})`}
              size="small"
            />
            &nbsp;{t("component:body.inDeliveredCountries")}&nbsp;
            <Chip
              label={`${isOriginalLanguageInCountries ? t("common:yes") : t("common:no")}`}
              size="small"
              color={isOriginalLanguageInCountries ? "success" : "default"}
            />
          </Stack>
        </FormSection>
        <FormSection
          icon={<ConfigFormIcon icon="video" />}
          title={t("form:section.title.mainOutputVideoResolution")}
          status={settings.targetFeatureVideoProfile ? "complete" : "todo"}
          statusText={settings.targetFeatureVideoProfile || t("common:configurationRequired")}
          defaultExpanded={false}
        >
          <FormAutocomplete
            label={t("field:label.selectOutputFeatureVideoResolution")}
            options={GlobalConst.BUNDLE_MAKER_ALLOWED_VIDEO_PROFILES}
            onChange={(v) => {
              dispatch(
                UpdateItemSettingsRootOnBasket({
                  itemId: item.id,
                  field: SettingRootField.TargetFeatureVideoProfile,
                  value: v,
                })
              );
            }}
            value={settings.targetFeatureVideoProfile}
            popupIcon={<ExpandMoreRoundedIcon />}
            isOptionEqualToValue={(option, _value) => option === _value}
            validationProps={{
              formId: formid,
              formSectionId: "sectionResolution",
              fieldId: "videoProfile",
              initialState: false,
              required: true,
            }}
          />
        </FormSection>
        {settings.needExternalBroadcasterId && (
          <FormSection
            icon={<ConfigFormIcon />}
            title={t("form:section.title.bundleIdentifier")}
            status={settings.externalBroadcasterId ? "complete" : "warning"}
            statusText={
              settings.externalBroadcasterId ? settings.externalBroadcasterId : t("common:configurationRequired")
            }
            defaultExpanded={false}
          >
            <FormInputText
              label={t("field:label.vendorId")}
              startAdornment={<TokenIcon />}
              placeholder={t("field:placeholder.enterVendorId")}
              value={settings.externalBroadcasterId}
              onChange={(v) => {
                dispatch(
                  UpdateItemSettingsRootOnBasket({
                    itemId: item.id,
                    field: SettingRootField.BroadcasterId,
                    value: v.target.value,
                  })
                );
              }}
              validationProps={{
                formId: formid,
                formSectionId: "sectionIdentifier",
                fieldId: "vendorId",
                initialState: false,
                required: settings.needExternalBroadcasterId,
              }}
              disabled={false}
            />
          </FormSection>
        )}
        <FormSection
          icon={<ConfigFormIcon icon="language" />}
          title={t("form:section.title.territoriesToDeliver")}
          status={settings.countries && settings.countries.length > 0 ? "complete" : "warning"}
          statusText={
            settings.countries?.map((territory) => territory.englishName).join(", ") ||
            t("common:configurationRequired")
          }
          defaultExpanded={false}
        >
          <Stack spacing={1}>
            {allTitleOriginalLanguages.length > 1 && (
              <FormAutocomplete
                label={"Select title original language"}
                options={allTitleOriginalLanguages}
                onChange={(v) => {
                  setSelectedTitleOriginalLang(v);
                }}
                value={titleOriginalLang}
                isOptionEqualToValue={(option, _value) => option.value === _value.value}
                getOptionLabel={(v: any) => v.label}
                popupIcon={<ExpandMoreRoundedIcon />}
                disableClearable
                validationProps={{
                  formId: formid,
                  formSectionId: "sectionCountries",
                  fieldId: "originalLanguage",
                  initialState: false,
                  required: true,
                }}
              />
            )}
            <FormAutocomplete
              label={t("field:label.whichTerritories")}
              options={cultures}
              multiple
              onChange={(v) => {
                dispatch(
                  UpdateItemSettingsRootOnBasket({
                    itemId: item.id,
                    field: SettingRootField.Countries,
                    value: v,
                  })
                );
              }}
              disabled={
                ((settings.objectId !== null && settings.objectId > 0) as boolean) ||
                (xmlProfile?.providerInfos?.territoriesCodes !== null &&
                  xmlProfile?.providerInfos?.territoriesCodes !== undefined &&
                  xmlProfile?.providerInfos?.territoriesCodes.length > 0)
              }
              value={settings.countries ?? []}
              popupIcon={<ExpandMoreRoundedIcon />}
              isOptionEqualToValue={(option, _value) => option.id === _value.id}
              getOptionLabel={(v: any) => v.englishName}
              filterOptions={(x, s) => x.filter((a) => a.englishName?.includesAllWords(s.inputValue))}
              validationProps={{
                formId: formid,
                formSectionId: "sectionCountries",
                fieldId: "countries",
                initialState: false,
                required: true,
              }}
            />
            {canUseBundlePackages && (
              <Box position={"relative"}>
                <IconButton
                  onClick={() => setLoadPackageTrigger((x) => x + 1)}
                  sx={{ position: "absolute", top: "-4px", right: 0, zIndex: 1000 }}
                >
                  <RefreshOutlined color="primary" />
                </IconButton>
                <FormAutocomplete
                  renderOption={(props, option) => {
                    const pckge = availablePackages.find((x) => x.id === option);
                    if (!pckge) return <></>;
                    const usedCultures = getAssetCultures(pckge, cultures);
                    const cultureLabelFull = usedCultures.map((x) => x.englishName).join(", ");
                    return (
                      <li {...props}>
                        <Stack justifyContent={"space-between"} width={"100%"} direction={"row"} spacing={0.5}>
                          <Typography variant="body1">{pckge?.name ?? t("common:lowerCaseUnknown")}</Typography>
                          <Stack direction={"row"} spacing={1}>
                            <Tooltip title={cultureLabelFull}>
                              <Chip label={cultureLabelFull} />
                            </Tooltip>
                            <AssetStatus status={pckge.status} />
                            {pckge.status !== ObjectStatus.Accepted && (
                              <Button
                                sx={{ height: "24px" }}
                                size="small"
                                startIcon={<InventoryIcon />}
                                color="primary"
                                href={"/titles/" + settings.titleId + "/bundlemaker"}
                                variant="outlined"
                                target="_blank"
                              >
                                BM
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </li>
                    );
                  }}
                  getOptionLabel={(option) => availablePackages.find((x) => x.id === option)?.name ?? "unknown"}
                  options={availablePackages
                    .filter((x) => {
                      const matchEpNb =
                        (x.metadata?.find((m) => m.id === epNbMetadata?.id)?.value ?? null) ===
                        (item.selectedProduct?.episodeNb ?? null);
                      const matchBroadcaster =
                        (x.metadata?.find((m) => m.id === broadcasterIdMeta?.id)?.value ?? null) ===
                        (item.selectedBroadcaster?.id ?? null);
                      return matchEpNb && matchBroadcaster;
                    })
                    .map((x) => x.id)}
                  validationProps={{
                    formId: formid,
                    formSectionId: "sectionCountries",
                    fieldId: "objectId",
                    initialState: false,
                    required: false,
                  }}
                  label={t("field:label.selectExistingPackage")}
                  value={settings.objectId ?? null}
                  onChange={(v) => {
                    const pckge = availablePackages.find((x) => x.id === v);
                    if (pckge && pckge.status !== ObjectStatus.Accepted) return;

                    const usedCultures = pckge ? getAssetCultures(pckge, cultures) : [];
                    dispatch(
                      UpdateItemSettingsRootOnBasket({
                        itemId: item.id,
                        field: SettingRootField.ObjectId,
                        value: v,
                      })
                    );
                    dispatch(
                      UpdateItemSettingsRootOnBasket({
                        itemId: item.id,
                        field: SettingRootField.Countries,
                        value: usedCultures,
                      })
                    );
                  }}
                />
              </Box>
            )}
          </Stack>
        </FormSection>
        <FormSection
          icon={<ConfigFormIcon icon="settings" />}
          title={t("form:section.title.composeYourBundlePackage")}
          status={formSectionComposer ? "complete" : "warning"}
          statusText={formSectionComposer ? t("common:configured") : t("common:configurationRequired")}
          defaultExpanded={false}
        >
          <Stack spacing={1}>
            {!lockComposer &&
              settings.components.map((component, index) => (
                <ConfigPackageBundleComponent
                  key={`component-${index}`}
                  _component={component}
                  missingContent={false}
                  onEdit={() => showModalComposePackageComponent(component)}
                  onDelete={() => dispatch(BundleMakerRemoveComponent({ itemId: item.id, componentIndex: index }))}
                />
              ))}
            {lockComposer && <Alert severity="info">{t("alert:info.selectTerritoriesToDeliver")}</Alert>}
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2} mt={2}>
            <Button
              startIcon={<AutorenewIcon />}
              variant="contained"
              size="small"
              onClick={makeDefaultBundle}
              disabled={lockComposer}
            >
              {t("button:makeDefaultPackage")}
            </Button>
            <Select
              displayEmpty
              size="small"
              IconComponent={ExpandMoreRoundedIcon}
              inputProps={{ "aria-label": "Without label" }}
              startAdornment={<AddIcon />}
              value={addComponentValue ?? "default"}
              disabled={lockComposer}
              defaultValue={"default"}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            >
              <MenuItem value={"default"}>{t("menu:item.addComponent")}</MenuItem>
              {Object.keys(TranskodeBundleComponentType)
                .filter(
                  (x) =>
                    xmlProfile?.components.map((c) => c.type).includes(x as TranskodeBundleComponentType) &&
                    !settings.components.map((c) => c.type).includes(x as TranskodeBundleComponentType)
                )
                .map((value, index) => (
                  <MenuItem
                    value={value}
                    key={index}
                    onClick={(e) => {
                      let profileComponent = xmlProfile?.components.find(
                        (x) => x.type === (value as TranskodeBundleComponentType)
                      );

                      if (profileComponent) {
                        dispatch(BundleMakerAddComponent({ itemId: item.id, component: profileComponent }));
                        setAddComponent(undefined);
                      }
                    }}
                  >
                    {value}
                  </MenuItem>
                ))}
            </Select>
          </Stack>
        </FormSection>
        <FormSection
          icon={<ConfigFormIcon icon="meta" />}
          title={t("common:metadata")}
          status={formSectionMetadata ? "complete" : "warning"}
          statusText={formSectionMetadata ? t("common:configured") : t("common:configurationRequired")}
          defaultExpanded={false}
        >
          <Stack spacing={1}>
            <ConfigPackageBundleMetadata
              title={t("common:commonMetadata")}
              onClick={() => settings.titleId && OpenLinkInNewTab("/titles/" + settings.titleId + "/bundlemaker")}
              status={
                checkValidationByCountry(null, metadataSource).filter(
                  (x) => !x.optional && x.status === BundleMetadataValidationStatus.Missing
                ).length === 0
                  ? "complete"
                  : "missing"
              }
            />
            {settings?.countries?.map((country, index) => (
              <ConfigPackageBundleMetadata
                key={index}
                title={country.englishName!}
                onClick={() => settings.titleId && OpenLinkInNewTab("/titles/" + settings.titleId + "/bundlemaker")}
                status={
                  checkValidationByCountry(
                    country,
                    metadataSource,
                    settings.objectId
                      ? availablePackages
                          .find((x) => x.id === settings.objectId)
                          ?.metadata?.find((x) => x.id === epNbMetadata?.id)?.value ?? null
                      : null
                  ).filter((x) => !x.optional && x.status === BundleMetadataValidationStatus.Missing).length === 0
                    ? "complete"
                    : "missing"
                }
              />
            ))}
          </Stack>
        </FormSection>
        <ConfigCommonSectionFormNexguardWatermark item={item} />
        {deliveryAttachments && <ConfigCommonSectionFormAttachments item={item} />}

        {UserHasAcl(AclTypes.aclOrderIngestGenerated) && <ConfigCommonSectionFormAssetsVisible item={item} />}
      </Stack>
    </StyledForm>
  );
};

export default ConfigPackageBundleForm;

const StyledForm = styled("form")(({ theme }) => ({}));
